/** Public site identity and company details — shared with Arboretum Labs products. */

export const siteName = "whatIsUp";

export const siteBrand = "whatIsUp";

export const legalEntityName = "Arboretum Labs Ltd";

export const companyNumber = "17364443";

export const companyRegistrationPlace = "England and Wales";

export const registeredOfficeAddress =
  "75 Marsh wall, London, E14 9TT, United Kingdom";

export const supportEmail = "hello@arboretumlabs.co.uk";

export const legalLastUpdated = "27 July 2026";

export const productionUrl = "https://whatisup.arboretumlabs.co.uk";

export type FooterLink = {
  label: string;
  to: string;
  external?: boolean;
};

export const footerProductLinks: FooterLink[] = [
  { label: "Explore", to: "/explore" },
  { label: "Science", to: "/science" },
  { label: "Reviews", to: "/reviews" },
  { label: "Labels & terminology", to: "/science#glossary" },
  {
    label: "MSci thesis PDF",
    to: "https://github.com/Vicster97/MSci_project_atmospheric_blocking/blob/master/MSci_project_code_SPAT-Voulgarakis-1_CID_01192437_final.pdf",
    external: true,
  },
];

/** Unique evidence and foundational sources used across Science and Explore. */
export const footerSourceLinks: FooterLink[] = [
  {
    label: "Thomas et al. (2021) · expert GTD and index disagreement",
    to: "https://wcd.copernicus.org/articles/2/581/2021/",
    external: true,
  },
  {
    label: "Woollings et al. (2018) · blocking / stationary-ridge review",
    to: "https://link.springer.com/article/10.1007/s40641-018-0108-z",
    external: true,
  },
  {
    label: "Rousi, Kornhuber, Coumou et al. (2022) · Eurasian double jets",
    to: "https://www.nature.com/articles/s41467-022-31432-y",
    external: true,
  },
  {
    label: "Tibaldi & Molteni (1990) · TM90",
    to: "https://doi.org/10.3402/tellusa.v42i3.11881",
    external: true,
  },
  {
    label: "Dole & Gordon (1983) · DG83",
    to: "https://doi.org/10.1175/1520-0493(1983)111%3C1567:POTNAA%3E2.0.CO;2",
    external: true,
  },
  {
    label: "Schwierz et al. (2004) · S04",
    to: "https://doi.org/10.1175/1520-0493(2004)132%3C1849:FDIB%3E2.0.CO;2",
    external: true,
  },
  {
    label: "Struzewska & Kaminski (2008) · 2006 Eastern European ridge",
    to: "https://acp.copernicus.org/articles/8/721/2008/",
    external: true,
  },
  {
    label: "NOAA Climate.gov · 2015 European omega block",
    to: "https://www.climate.gov/news-features/event-tracker/summer-heat-wave-arrives-europe",
    external: true,
  },
  {
    label: "Cattiaux & Yiou (2013) · 2011 summer circulation",
    to: "https://doi.org/10.1175/BAMS-D-12-00021.1",
    external: true,
  },
  {
    label: "Sibley (2012) · Spanish-plume thunderstorms",
    to: "https://rmets.onlinelibrary.wiley.com/doi/10.1002/wea.1928",
    external: true,
  },
  {
    label: "WMO Region VI (2011) · European summer bulletin",
    to: "https://www.dwd.de/EN/ourservices/ravibulletinjahr/archiv/bulletin_2011.pdf?__blob=publicationFile&v=4",
    external: true,
  },
  {
    label: "New Scientist · Cuff 2025",
    to: "https://www.newscientist.com/article/2463818-is-a-broken-jet-stream-causing-extreme-weather-that-lasts-longer/",
    external: true,
  },
  {
    label: "Copernicus (2026) · May heatwave impacts",
    to: "https://climate.copernicus.eu/what-do-we-know-about-europes-early-and-intense-heatwave-may-2026",
    external: true,
  },
  {
    label: "Met Office (2026) · late-spring UK heatwave",
    to: "https://www.metoffice.gov.uk/api/assets/file/2026_05_may_heatwave_1pdf?prefix=assets",
    external: true,
  },
  {
    label: "UKHSA (2026) · provisional May–June mortality",
    to: "https://www.gov.uk/government/publications/interim-heat-mortality-monitoring-report-england-may-and-june-2026/interim-heat-mortality-monitoring-report-england-may-and-june-2026",
    external: true,
  },
  {
    label: "WMO (2026) · hottest June on record",
    to: "https://wmo.int/media/news/western-europe-has-hottest-june-record",
    external: true,
  },
  {
    label: "WHO Europe (2026) · June heatwave mortality assessment",
    to: "https://cdn.who.int/media/docs/default-source/_sage-2026/20260723_who-rapid-risk-assessment_-heatwave_euro_v.1.pdf?download=true&sfvrsn=c213a741_3",
    external: true,
  },
  {
    label: "CEDIM (2026) · June blocking-dominated circulation",
    to: "https://www.cedim.kit.edu/download/CEDIM-FDA-Report_HeatWaveJune2026.pdf",
    external: true,
  },
  {
    label: "Met Office (2026) · 37-day UK heatwave spell",
    to: "https://www.metoffice.gov.uk/blog/2026/uk-heatwaves-how-the-records-tell-a-changing-story",
    external: true,
  },
  {
    label: "WMO (2026) · heat, drought and cascading impacts",
    to: "https://wmo.int/media/news/record-breaking-heat-and-extreme-weather-continue",
    external: true,
  },
  {
    label: "Euronews / Triodos (2026) · provisional €180bn EU estimate",
    to: "https://www.euronews.com/business/2026/08/12/europes-scorching-summer-could-cost-eu-economies-180-billion",
    external: true,
  },
  {
    label: "Guardian (2026) · provisional Europe-wide excess mortality",
    to: "https://www.theguardian.com/environment/2026/aug/25/at-least-35000-excess-deaths-europe-back-to-back-heatwaves",
    external: true,
  },
];

export const footerSupportLinks: FooterLink[] = [
  {
    label: `Email: ${supportEmail}`,
    to: `mailto:${supportEmail}`,
    external: true,
  },
];
